import React, { useState } from 'react';
import {
  Grid,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  DialogContentText,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Checkbox,
  Box,
} from '@mui/material';
import { color } from '../../theme';
import { useTranslation } from 'react-i18next';
import { Subscription, SubscriptionType, User } from '../../types';
import { format, isAfter } from 'date-fns';
import {
  cancelUserSubscription,
  deleteUserSubscription,
  fetchSubscriptionTypes,
  forceSubscriptionExpiry,
} from 'services/subscriptionApi';
import { useMst } from '../../model/Root';
import { getSingleUser } from 'services/api';
import { observer } from 'mobx-react-lite';
import EditIcon from '../../static/edit.svg';

interface UserSubscriptionsProps {
  reloadUser: () => Promise<void>;
  currentUser: User;
}

const formatDateInput = (value: string | Date) => {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
};

const UserSubscriptions: React.FC<UserSubscriptionsProps> = ({ reloadUser, currentUser }: UserSubscriptionsProps) => {
  const { t } = useTranslation();
  const {
    userStore: { updateUser },
    errorStore: { setError, setSuccess },
  } = useMst();

  const subscriptions = currentUser?.subscriptions;
  const [selectedSubId, setSelectedSubId] = useState(0);
  const [openAdd, setOpenAdd] = useState(false);
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<number | ''>('');
  const [validMonths, setValidMonths] = useState<number | ''>('');
  const [isAddingSubscription, setIsAddingSubscription] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [selectedRemoveSubId, setSelectedRemoveSubId] = useState(0);
  const [isRemovingSubscription, setIsRemovingSubscription] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [forceExpiryDate, setForceExpiryDate] = useState('');
  const [forceExpiryMarkInactive, setForceExpiryMarkInactive] = useState(true);
  const [isForceExpirySaving, setIsForceExpirySaving] = useState(false);
  const [forceExpiryError, setForceExpiryError] = useState<string | null>(null);

  const fetchTypes = async () => {
    try {
      const response = await fetchSubscriptionTypes();
      if (response && Array.isArray(response)) {
        setSubscriptionTypes(response);
      }
    } catch (error) {
      console.error('Failed to fetch subscription types', error);
      setSubscriptionTypes([]);
    }
  };

  const handleClickOpen = (id: number) => {
    setSelectedSubId(id);
  };

  const handleClose = () => {
    setSelectedSubId(0);
  };

  const handleAddOpen = () => {
    fetchTypes();
    setOpenAdd(true);
  };

  const handleAddClose = () => {
    setOpenAdd(false);
    setSelectedSubscriptionType('');
    setValidMonths('');
  };

  const handleRemoveOpen = (id: number) => {
    setSelectedRemoveSubId(id);
  };

  const handleRemoveClose = () => {
    setSelectedRemoveSubId(0);
  };

  const handleEditOpen = (subscription: Subscription) => {
    setEditingSubscription({ ...subscription });
    setForceExpiryDate(subscription.periodEnds ? formatDateInput(subscription.periodEnds) : '');
    setForceExpiryMarkInactive(true);
    setForceExpiryError(null);
  };

  const handleEditClose = () => {
    setEditingSubscription(null);
    setForceExpiryDate('');
    setForceExpiryMarkInactive(true);
    setForceExpiryError(null);
    setIsForceExpirySaving(false);
  };

  const handleForceExpirySave = async () => {
    if (!editingSubscription) {
      return;
    }
    if (!forceExpiryDate) {
      setForceExpiryError(t('portal_users.fix_missing_end_date_period_required'));
      return;
    }
    const parsed = new Date(forceExpiryDate);
    if (Number.isNaN(parsed.getTime())) {
      setForceExpiryError(t('portal_users.fix_missing_end_date_period_required'));
      return;
    }
    const parsedValue = parsed.toISOString();
    setForceExpiryError(null);
    setIsForceExpirySaving(true);
    try {
      await forceSubscriptionExpiry({
        subscriptionId: editingSubscription.id,
        userId: currentUser.id ?? undefined,
        periodEnds: parsedValue,
        valid: forceExpiryMarkInactive ? false : undefined,
      });
      await reloadUser();
      setSuccess('portal_users.force_expiry_success');
      handleEditClose();
    } catch (error) {
      console.error('UserSubscriptions: Failed to force expiry date', error);
      setError('portal_users.force_expiry_error');
    } finally {
      setIsForceExpirySaving(false);
    }
  };

  const handleSubscriptionCancel = async () => {
    setIsCancellingSubscription(true);
    const cancelledId = selectedSubId;

    try {
      await cancelUserSubscription(selectedSubId);

      // Refresh user data to show subscription removal
      await reloadUser();

      // Show success message
      setSuccess('cancel_success');

      // Close dialog
      setSelectedSubId(0);
      if (editingSubscription && editingSubscription.id === cancelledId) {
        handleEditClose();
      }
    } catch (error) {
      console.error('UserSubscriptions: Failed to cancel subscription', error);
      setError('cancel_error');
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleSubscriptionRemove = async () => {
    setIsRemovingSubscription(true);
    const removedId = selectedRemoveSubId;

    try {
      await deleteUserSubscription(selectedRemoveSubId);

      // Refresh user data to show subscription removal
      await reloadUser();

      setSuccess('delete_success');
      setSelectedRemoveSubId(0);
      if (editingSubscription && editingSubscription.id === removedId) {
        handleEditClose();
      }
    } catch (error) {
      console.error('UserSubscriptions: Failed to delete subscription', error);
      setError('delete_error');
    } finally {
      setIsRemovingSubscription(false);
    }
  };

  const handleAddSubscription = async () => {
    if (!selectedSubscriptionType || !validMonths || !currentUser.id) return;

    setIsAddingSubscription(true);

    try {
      // 1) Fetch RAW user from backend to construct a safe, backend-shaped payload
      const raw = await getSingleUser(String(currentUser.id));
      const u = raw.data;

      // 2) Build safe payload by whitelisting backend-expected fields
      const allowedKeys = [
        'id',
        'firstname',
        'lastname',
        'email',
        'address',
        'postalcode',
        'city',
        'country',
        'phone',
        'admin',
        'publisher',
        'creator',
        'employee',
        'portal',
        'subscriber',
        'tester',
        'permission',
        'marketing',
        'newsletter',
        'empNewsletter',
        'emailVerified',
        'customerId',
        'ageGroup',
        'gender',
        'communityIds',
        'nextUsageResetDate',
      ] as const;

      const merged: Partial<User> & Record<string, unknown> = {};
      allowedKeys.forEach((k) => {
        const value = (u as Record<string, unknown>)[k as unknown as string];
        if (value !== undefined) merged[k as unknown as string] = value;
      });

      // Ensure correct types
      merged.firstname = String(merged.firstname ?? '');
      merged.lastname = String(merged.lastname ?? '');
      merged.email = String(merged.email ?? '');
      merged.address = (merged.address as string) ?? '';
      merged.postalcode = (merged.postalcode as string) ?? '';
      merged.city = (merged.city as string) ?? '';
      merged.country = (merged.country as string) ?? '';
      merged.phone = (merged.phone as string) ?? '';
      (
        [
          'admin',
          'publisher',
          'creator',
          'employee',
          'portal',
          'subscriber',
          'tester',
          'permission',
          'marketing',
          'newsletter',
          'empNewsletter',
          'emailVerified',
        ] as const
      ).forEach((bk) => {
        const key = bk as unknown as string;
        merged[key] = !!(merged[key] as boolean);
      });
      if (!Array.isArray(merged.communityIds)) merged.communityIds = [];

      // Apply subscription changes
      merged.subscriber = true;
      merged.subscriptionTypeId = Number(selectedSubscriptionType);
      merged.subscriptionMonths = Number(validMonths);

      // 3) PUT full merged user (omit password entirely)
      await updateUser(u.id, merged as Partial<User>);

      // Refresh user data to show new subscription
      await reloadUser();

      setSuccess('save_success');
      handleAddClose();
    } catch (error) {
      console.error('UserSubscriptions: Failed to add subscription', error);
      setError('save_error');
    } finally {
      setIsAddingSubscription(false);
    }
  };

  const getPaymentType = (type?: number) => {
    if (type === undefined || type === null) return '';

    switch (type) {
      case 1:
        return 'Stripe';
      case 2:
        return 'Koodi';
      case 3:
        return 'Lahja';
      case 4:
        return 'Admin';
      case 5:
        return 'Apple';
      case 6:
        return 'Google';
      case 0:
        return 'Perheenjäsen/ilmainen';
      default:
        return '';
    }
  };

  const getSubscriptionTypeLabel = (sub: Subscription) => {
    const name = sub.subscriptionType?.name;
    if (name && name.trim().length > 0) return name;

    const type = sub.subscriptionType?.type;
    switch (type) {
      case 1:
        return t('subscription_form_month');
      case 2:
        return t('subscription_form_year');
      case 3:
        return t('subscription_form_gift');
      case 4:
        return t('subscription_form_preview');
      default:
        return '-';
    }
  };

  const getStatus = (sub: Subscription) => {
    const now = new Date();
    const ends = sub.periodEnds ? new Date(sub.periodEnds) : null;

    if (sub.valid === false) {
      return { label: 'peruttu', color: 'red' } as const;
    }

    if (ends && !isAfter(ends, now)) {
      return { label: 'vanhentunut', color: 'gray' } as const;
    }

    return { label: 'voimassa', color: 'green' } as const;
  };

  const getCode = (sub: Subscription) => {
    return sub.subscriptionCode || sub.platformSubscriptionId || sub.stripeSubscriptionId || '—';
  };

  return (
    <Grid container>
      <Grid item xs={12} mt={3} mb={1} display="flex" justifyContent="space-between">
        <Typography variant="h5">{t('subscriptions')}</Typography>
        <Button onClick={handleAddOpen} style={{ backgroundColor: color.primaryButton, color: color.white }}>
          {t('add_subscription')}
        </Button>
      </Grid>
      <Grid container item xs={12} fontWeight={'bold'}>
        <Grid item xs={12} md={2}>
          {t('subscription_type')}
        </Grid>
        <Grid item xs={12} md={1}>
          {t('status')}
        </Grid>
        <Grid item xs={12} md={2}>
          {t('subscription_period')}
        </Grid>
        <Grid item xs={12} md={2}>
          Tilauskoodi
        </Grid>
        <Grid item xs={12} md={2}>
          Maksumuoto
        </Grid>
        <Grid item xs={12} md={1}>
          Omistaja
        </Grid>
        <Grid item xs={12} md={2}></Grid>
      </Grid>
      {(!subscriptions || subscriptions.length == 0) && (
        <Grid container item xs={12}>
          <Grid item xs={12} md={2}>
            <Typography fontWeight={500}>{t('no_subcsriptions')}</Typography>
          </Grid>
        </Grid>
      )}
      {subscriptions?.map((sub) => (
        <Grid container item xs={12} key={sub.id} alignItems="center">
          <Grid item xs={12} md={2}>
            <Typography fontWeight={500}>{getSubscriptionTypeLabel(sub)}</Typography>
          </Grid>
          <Grid item xs={12} md={1}>
            {(() => {
              const status = getStatus(sub);
              return (
                <Typography variant="caption" sx={{ color: status.color }}>
                  {status.label}
                  {status.label === 'voimassa' && sub.autoRenews === false ? ' • Automaattinen uusinta pois' : ''}
                </Typography>
              );
            })()}
          </Grid>
          <Grid item xs={12} md={2}>
            {format(new Date(sub.periodStarts), 'd.M.yyyy')} -{' '}
            {sub.periodEnds == null || sub.periodEnds === '' ? '—' : format(new Date(sub.periodEnds), 'd.M.yyyy')}
          </Grid>
          <Grid item xs={12} md={2}>
            {getCode(sub)}
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="darkblue">
              {getPaymentType(sub.paymentType)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={1}>
            {sub.ownerId ?? '—'}
          </Grid>
          <Grid item xs={12} md={2} display="flex" justifyContent="flex-end">
            <IconButton onClick={() => handleEditOpen(sub)} aria-label={t('edit')}>
              <img src={EditIcon} alt={t('edit') ?? 'Edit'} />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Dialog open={Boolean(editingSubscription)} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="h6" component="span">
            {t('user_subscriptions.edit_title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {editingSubscription?.valid && (
              <Button
                onClick={() => {
                  if (editingSubscription) {
                    handleClickOpen(editingSubscription.id);
                  }
                }}
                color="warning"
                variant="outlined"
              >
                {t('cancel_subscription')}
              </Button>
            )}
            <Button
              onClick={() => {
                if (editingSubscription) {
                  handleRemoveOpen(editingSubscription.id);
                }
              }}
              color="error"
              variant="contained"
            >
              {t('remove_subscription')}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {editingSubscription ? getSubscriptionTypeLabel(editingSubscription) : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('portal_users.fix_missing_end_date_description')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label={t('portal_users.fix_missing_end_date_period_label')}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={forceExpiryDate}
              onChange={(event) => {
                setForceExpiryDate(event.target.value);
                setForceExpiryError(null);
              }}
              error={Boolean(forceExpiryError)}
              helperText={forceExpiryError || t('portal_users.fix_missing_end_date_period_helper')}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={forceExpiryMarkInactive}
                onChange={(event) => setForceExpiryMarkInactive(event.target.checked)}
              />
            }
            label={t('portal_users.fix_missing_end_date_mark_inactive')}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleEditClose}>{t('cancel')}</Button>
          <Button onClick={handleForceExpirySave} variant="contained" disabled={isForceExpirySaving}>
            {isForceExpirySaving && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {t('portal_users.fix_missing_end_date_confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={selectedSubId > 0} onClose={handleClose}>
        <DialogTitle>{t('cancel_subscription_confirm_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('cancel_subscription_confirm_text')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} style={{ color: color.greyText }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubscriptionCancel}
            color="error"
            disabled={isCancellingSubscription}
            startIcon={isCancellingSubscription ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isCancellingSubscription ? t('saving') : t('cancel_subscription')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={selectedRemoveSubId > 0} onClose={handleRemoveClose}>
        <DialogTitle>{t('remove_subscription_confirm_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('remove_subscription_confirm_text')}</DialogContentText>
          <Box mt={2}>
            <DialogContentText color="error">{t('user_subscriptions.delete_subscription_note')}</DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveClose} style={{ color: color.greyText }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubscriptionRemove}
            color="error"
            disabled={isRemovingSubscription}
            startIcon={isRemovingSubscription ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isRemovingSubscription ? t('deleting') : t('remove_subscription')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openAdd} onClose={handleAddClose} fullWidth maxWidth="xs">
        <DialogTitle>{t('add_subscription')}</DialogTitle>
        <DialogContent>
          <TextField
            select
            label={t('subscription_type')}
            value={selectedSubscriptionType}
            onChange={(e) => setSelectedSubscriptionType(e.target.value as unknown as number)}
            fullWidth
            variant="outlined"
            margin="normal"
          >
            {Array.isArray(subscriptionTypes) &&
              subscriptionTypes.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            type="number"
            label={t('subscription_months')}
            value={validMonths}
            onChange={(e) => setValidMonths(Number(e.target.value))}
            fullWidth
            variant="outlined"
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose} style={{ color: color.greyText }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleAddSubscription}
            color="primary"
            variant="contained"
            disabled={!selectedSubscriptionType || !validMonths || isAddingSubscription}
            startIcon={isAddingSubscription ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isAddingSubscription ? t('saving') : t('add_subscription')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default observer(UserSubscriptions);
